<template>
  <footer class="container-fluid px-0 py-3 pt-5">
    <div ref="footerRef" class="footer container">
      <!-- Feature strip -->
      <div class="feature-strip">
        <div class="feature-strip-inner">
          <div class="feature-item">
            <i class="fas fa-leaf feature-icon"></i>
            <span>Asesoramiento personalizado</span>
          </div>
          <div class="feature-item">
            <i class="fas fa-box feature-icon"></i>
            <span>Envíos a todo el país</span>
          </div>
          <div class="feature-item">
            <i class="fas fa-shield-alt feature-icon"></i>
            <span>Distribuidora oficial</span>
          </div>
          <div class="feature-item">
            <i class="fas fa-shopping-bag feature-icon"></i>
            <span>Venta mayorista</span>
          </div>
        </div>
      </div>
  
      <!-- Main footer body -->
      <div class="footer-body">
        <div class="footer-main">
          <!-- Contact column -->
          <div class="footer-contact">
            <h2 class="contact-title text-white">CONTACTO</h2>
            <div class="contact-info mt-3">
              <div class="contact-item d-flex gap-3">
                <i class="fas fa-envelope mt-1"></i>
                <a class="text-white text-decoration-none" :href="contactMailto">{{ contactEmail }}</a>
              </div>
              <div class="contact-item d-flex gap-3">
                <i class="fas fa-phone mt-1"></i>
                <a class="text-white text-decoration-none" :href="telHref">{{ contactPhoneDisplay }}</a>
              </div>
              <div v-if="instagramUrl" class="contact-item d-flex gap-3">
                <i class="fab fa-instagram mt-1"></i>
                <a class="text-white text-decoration-none" :href="instagramUrl" target="_blank" rel="noopener noreferrer">Instagram</a>
              </div>
            </div>
          </div>
  
          <!-- Link columns -->
          <div class="footer-links-grid">
            <div class="footer-col">
              <h4 class="footer-col-title">Productos</h4>
              <ul class="footer-col-links">
                <li><a href="/productos">Ver todos</a></li>
                <li v-for="cat in footerCategories" :key="cat.id">
                  <a :href="'/productos?categories=' + encodeURIComponent(cat.name)">{{ cat.name }}</a>
                </li>
              </ul>
            </div>
            <div class="footer-col">
              <h4 class="footer-col-title">Nosotros</h4>
              <ul class="footer-col-links">
                <li><a href="/nosotros">Quiénes somos</a></li>
                <li><a href="/contacto">Contacto</a></li>
              </ul>
            </div>
            <div class="footer-col">
              <h4 class="footer-col-title">Ayuda</h4>
              <ul class="footer-col-links">
                <li><a href="/contacto">Preguntas frecuentes</a></li>
                <li><a href="/carrito/">Carrito</a></li>
                <li><a href="/terminos-y-condiciones">Términos y condiciones</a></li>
              </ul>
            </div>
          </div>
        </div>
  
        <!-- Bottom bar -->
        <div class="footer-bottom">
          <span>&copy; {{ currentYear }} {{ storeName }}. Todos los derechos reservados</span>
          <div class="dev pe-lg-5 pe-0">
            <span>Desarrollado por</span>
            <a v-if="developerUrl" :href="developerUrl" target="_blank" rel="noopener noreferrer" class="dev-link">
              <img :src="permacultureLogo" alt="Permaculture" class="developer-logo">
            </a>
            <img v-else :src="permacultureLogo" alt="Permaculture" class="developer-logo">
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { useScrollReveal } from '@/composables/useScrollReveal';
import { getCategories } from '@/services/categories';
import { useSiteContact } from '@/composables/useSiteContact';
import permacultureLogo from '@/assets/images/permaculture-logo.svg';

const currentYear = new Date().getFullYear();

const {
  contactEmail,
  contactPhoneDisplay,
  contactMailto,
  telHref,
  instagramUrl,
  storeName,
  developerUrl,
} = useSiteContact();

const footerRef = ref(null);
const categories = ref([]);
const { observe } = useScrollReveal();

// Solo categorías padre (sin parent) para el footer, máximo 6
const footerCategories = computed(() => {
  const parents = categories.value.filter(cat => !cat.parent);
  return parents.slice(0, 3);
});

onMounted(async () => {
  if (footerRef.value) {
    observe(footerRef.value);
  }
  try {
    const response = await getCategories();
    const data = response.results || response;
    categories.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('Error cargando categorías en footer:', err);
  }
});
</script>

<style scoped>
footer{
  background-color: var(--black-color-2);
}
/* ── Feature strip ── */
.feature-strip {
  padding: 0;
}

.feature-strip-inner {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin: 0 auto;
  gap: 10px;
}

.feature-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem 1rem;
  color: var(--footer-text, #ccc);
  background-color: rgba(255, 255, 255, 0.08);
  font-size: 1rem;
  text-align: center;
  transition: background-color 0.2s;
  border-radius: 5px;
  font-weight: 500;
}

.feature-item i{
  color: var(--primary-light);
}

.feature-item:last-child {
  border-right: none;
}


.feature-icon {
  font-size: 1.5rem;
  color: var(--footer-text, #ccc);
  opacity: 0.8;
}

/* ── Main footer body ── */
footer{
  background-color: var(--black-color-2);
}
.footer {
  color: var(--footer-text, #ccc);
  margin-top: 0;
  border-top: none;
}

.footer-body {
  margin: 0 auto;
}

.footer-main {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4rem;
  padding: 3.5rem 0 3rem;
  align-items: start;
}

/* ── Newsletter ── */
.newsletter-title {
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--footer-heading, #fff);
  letter-spacing: 0.01em;
  margin-bottom: 1.5rem;
  line-height: 1.2;
}

.newsletter-form {
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(255,255,255,0.35);
  padding-bottom: 0.5rem;
  gap: 0;
  max-width: 450px;
}

.newsletter-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--footer-text, #ccc);
  font-size: 0.95rem;
  padding: 0.25rem 0;
}

.newsletter-input::placeholder {
  color: rgba(255,255,255,0.4);
}

.newsletter-btn {
  background: transparent;
  border: none;
  outline: none;
  color: var(--footer-heading, #fff);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.25rem 0 0.25rem 1rem;
  letter-spacing: 0.03em;
  transition: opacity 0.2s;
  white-space: nowrap;
}

.newsletter-btn:hover {
  opacity: 0.7;
}

.newsletter-desc {
  margin-top: 1.2rem;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.45);
  max-width: 450px;
  line-height: 1.6;
}

/* ── Link columns ── */
.footer-links-grid {
  display: grid;
  grid-template-columns: repeat(3, max-content);
  gap: 0 3rem;
}

.footer-col-title {
  color: var(--footer-heading, #fff);
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 1rem;
  letter-spacing: 0.04em;
}

.footer-col-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-col-links li {
  margin-bottom: 0.65rem;
}

.footer-col-links a {
  color: rgba(255,255,255,0.55);
  text-decoration: none;
  font-size: 0.85rem;
  transition: color 0.2s;
}

.footer-col-links a:hover {
  color: var(--footer-heading, #fff);
}

/* ── Bottom bar ── */
.footer-bottom {
  border-top: 1px solid rgba(255,255,255,0.1);
  padding: 1.2rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.45);
  gap: 1rem;
  flex-wrap: wrap;
}

.dev {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.82rem;
  color: #777;
  margin-left: auto;
}

.dev-link {
  color: #777;
  text-decoration: none;
  transition: color 0.2s;
}

.dev-link:hover {
  color: #999;
}

.developer-logo {
  display: block;
  width: 126px;
  filter: grayscale(1) brightness(0.45);
  height: auto;
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .footer-main {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }

  .feature-strip-inner {
    grid-template-columns: repeat(2, 1fr);
  }

  .feature-item {
    border-bottom: 1px solid rgba(255,255,255,0.08);
  }

  .feature-item:nth-child(2),
  .feature-item:nth-child(4) {
    border-right: none;
  }
}

@media (max-width: 600px) {
  .dev {
    margin-left: 0;
  }

  .feature-strip-inner {
    grid-template-columns: 1fr 1fr;
  }

  .footer-links-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem 2rem;
  }

  .newsletter-title {
    font-size: 1.25rem;
  }
}
</style>
